import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Lock, ArrowLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import DashboardContainer from "@/components/shared/layout/DashboardContainer";
import PageHeader from "@/components/shared/layout/PageHeader";
import { Card, CardContent } from "@/components/shared/ui/Card";

interface DocumentSection {
  id: string;
  section_id: string;
  title: string;
  content: string;
  color: string;
  order_index: number;
  is_locked: boolean;
  privacy_level?: string;
  allowed_access_levels?: string[]; // NEW
}

interface Document {
  id: string;
  name: string;
  document_type: string;
  project_id: string;
}

interface Project {
  id: string;
  name: string;
}

const InteractiveDocument: React.FC = () => {
  const { projectId, documentId } = useParams<{
    projectId: string;
    documentId: string;
  }>();
  const navigate = useNavigate();

  const [document, setDocument] = useState<Document | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [sections, setSections] = useState<DocumentSection[]>([]);
  const [userAccessLevel, setUserAccessLevel] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocumentAndSections();
  }, [documentId, projectId]);

  const fetchDocumentAndSections = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user and their access level
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        setError("User not found");
        return;
      }

      // Fetch user's access level
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("access_level")
        .eq("id", currentUser.id)
        .single();

      if (userError) throw userError;
      setUserAccessLevel(userData?.access_level || null);

      // Fetch document details
      const { data: docData, error: docError } = await supabase
        .from("documents")
        .select("*")
        .eq("id", documentId)
        .single();

      if (docError) throw docError;
      setDocument(docData);

      // Fetch project details
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("id, name")
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;
      setProject(projectData);

      // Fetch sections with allowed_access_levels
      const { data: sectionsData, error: sectionsError } = await supabase
        .from("document_sections")
        .select("*")
        .eq("document_id", documentId)
        .order("order_index", { ascending: true });

      if (sectionsError) throw sectionsError;
      setSections(sectionsData || []);
    } catch (err: any) {
      console.error("Error fetching document:", err);
      setError(err.message || "Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleBackToProject = () => {
    navigate(`/investor/project/${projectId}`);
  };

  // Check if user has access to a section based on their access_level
  const hasAccessToSection = (section: DocumentSection): boolean => {
    // If no allowed_access_levels specified, default to unlocked
    if (
      !section.allowed_access_levels ||
      section.allowed_access_levels.length === 0
    ) {
      return true;
    }

    // If user has no access_level, deny access
    if (!userAccessLevel) {
      return false;
    }

    // Check if user's access_level is in the allowed list
    return section.allowed_access_levels.includes(
      userAccessLevel.toLowerCase()
    );
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  if (error || !document) {
    return (
      <DashboardContainer>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold mb-2">
              Failed to load document
            </p>
            <p className="text-red-500 text-sm mb-4">{error}</p>
            <button
              onClick={handleBackToProject}
              className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 transition"
            >
              Back to Project
            </button>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <button
          onClick={handleBackToProject}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to {project?.name}</span>
        </button>

        {/* Document Header */}
        <div className="mb-8">
          <PageHeader title={`${document.name} - ${project?.name}`} />
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => {
            const isExpanded = expandedSections[section.section_id];

            // Check access based on access_level OR is_locked OR privacy_level
            const hasAccess = hasAccessToSection(section);
            const isLocked =
              !hasAccess ||
              section.is_locked ||
              section.privacy_level === "restricted" ||
              section.privacy_level === "confidential";

            return (
              <Card key={section.id} className="overflow-hidden">
                <div
                  className={`p-4 border-b-2 border-black cursor-pointer transition-all hover:opacity-90 ${
                    section.color || "bg-gray-100"
                  }`}
                  onClick={() => !isLocked && toggleSection(section.section_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isLocked && <Lock className="w-5 h-5 text-gray-600" />}
                      <h3 className="text-lg font-semibold text-gray-800">
                        {section.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isLocked &&
                        (isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        ))}
                    </div>
                  </div>
                </div>

                {isExpanded && !isLocked && (
                  <CardContent className="p-6 bg-white">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </CardContent>
                )}

                {isLocked && (
                  <CardContent className="p-6 bg-gray-50 text-center">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-600 font-medium">
                      This section is restricted
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {!hasAccess
                        ? "Your access level does not permit viewing this section"
                        : "Contact administrator for access"}
                    </p>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {sections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              No sections available for this document.
            </p>
          </div>
        )}
      </div>
    </DashboardContainer>
  );
};

export default InteractiveDocument;
